import { useEffect } from 'react';
import { useNavigationType } from 'react-router-dom';
import { SiteHeader, SiteFooter, FloatingCta, useTheme } from './Legal';
import LeadForm from './LeadForm';

// ── CONTENT ───────────────────────────────────────────────────────────────────
const META = [['Price', 'Fixed quote'], ['Delivery', 'Date agreed in writing'], ['Payment', '50% deposit, 50% on delivery'], ['Platforms', 'MT5 and MT4']];
const GET = [
  ['Written specification', 'Every entry, exit, filter and risk rule in plain language. You approve it before any code is written.'],
  ['Expert Advisor', 'Built for MT5 or MT4, with adjustable inputs for lot size or risk percentage, stop loss, take profit, sessions and instruments.'],
  ['Backtest report', 'From the MT5 Strategy Tester, with the settings documented so you can repeat the test yourself.'],
  ['Two rounds of changes', 'After you review the results, we adjust the EA to match your specification.'],
  ['Setup guide and support', 'A short guide to install and configure the EA, plus 14 days of support after delivery.'],
  ['Optional add-ons', 'A trade management panel, and licence-key protection if you distribute the EA to students or members. Quoted separately.'],
];
const STEPS = [
  ['A short call', 'We check that your strategy can be automated as described.', 'Free'],
  ['Specification and quote', 'You receive a written spec, a fixed price and a delivery date. Work starts after you approve it.', '50% deposit'],
  ['Build and test', 'We build the EA to the spec and run the backtests.', 'Nothing due'],
  ['Review and delivery', 'You review the results and request changes.', 'Balance on delivery'],
];
const WHO = [
  ['Strategy owners', 'You have rules that work and no time to watch every chart. The EA trades them the same way every time.', 'Run your rules without watching charts.'],
  ['Trading educators', 'Give students a working version of the strategy you teach, so they can see it run and not only read about it.', 'Give students something that runs.'],
  ['Signal providers', 'Let members execute your rules automatically instead of copying signals by hand.', 'Let members execute automatically.'],
];
const TERMS = [['Price', 'Fixed, quoted after a short call'], ['Delivery', 'A date agreed in writing'], ['Payment', '50% deposit, 50% on delivery'], ['Confidentiality', 'We sign an NDA before you share details'], ['Approval', 'Nothing is built until you approve the specification']];
const FAQ = [
  ['Will the EA make money?', 'We cannot promise that. The EA follows your rules exactly. Results depend on the strategy and on market conditions, and backtest results do not predict future performance.'],
  ['Will my strategy stay confidential?', 'Yes. We sign an NDA before you share details, and ownership of the delivered code is set out in the written agreement before work starts.'],
  ['What if my strategy cannot be automated?', 'We find that out on the call, before you pay anything. Rules that depend on discretion or news are sometimes not practical to code, and we will tell you plainly.'],
  ['What does it cost?', 'A fixed quote after the call, based on how complex the strategy is. There are no hourly surprises.'],
  ['Which platforms do you build for?', 'MetaTrader 5 and MetaTrader 4 as standard. Other platforms are possible on request.'],
];

// Design follows Strategy_Research_Report_v2.html: Syne for the logo and headings, Calibri-stack body, 16px base.
export const CSS = `
.ea-root{--ink:#111111;--paper:#ffffff;--bg2:#f6f6f4;--teal:#044D41;--accent:#044D41;--eyebrow:#B8860B;--warn:#8a6508;--muted:#65635e;--border:#e4e2de;--good:rgba(4,77,65,.08);--warnbg:rgba(184,134,11,.10);min-height:100vh;background:var(--paper);color:var(--ink);color-scheme:light;font:16px/1.7 Calibri,Candara,Segoe,'Segoe UI',Optima,Arial,sans-serif;transition:background .2s,color .2s}
.ea-root[data-theme=dark]{--eyebrow:#00c9a7;--ink:#f0f0f5;--paper:#050506;--bg2:#0d0d10;--teal:#00c9a7;--accent:#00c9a7;--warn:#f5a623;--muted:#8888a0;--border:#252530;--good:rgba(0,201,167,.12);--warnbg:rgba(245,166,35,.10);color-scheme:dark}
.ea-root *,.ea-root *::before,.ea-root *::after{box-sizing:border-box;margin:0;padding:0}
.ea-root a{color:inherit}
.ea-root p a,.ea-root :focus-visible{outline:2px solid var(--accent);outline-offset:3px}
.ea-page{max-width:920px;margin:0 auto;padding:64px 36px 80px}
.ea-cover{padding:56px 0 52px;border-bottom:2px solid var(--teal);margin-bottom:48px}
.ea-tag{font-family:'Syne',sans-serif;letter-spacing:.2em;text-transform:uppercase;color:var(--muted);margin-bottom:20px}
.ea-tag span{color:var(--accent)}
.ea-title{font-family:'Syne',sans-serif;font-size:36px;font-weight:700;line-height:1.1;margin-bottom:12px}
.ea-sub{font-size:18px;color:var(--muted);font-style:italic;margin-bottom:36px}
.ea-meta{display:flex;gap:40px;flex-wrap:wrap}
.ea-meta div{display:flex;flex-direction:column;gap:3px}
.ea-meta .l{font-family:'Syne',sans-serif;letter-spacing:.15em;text-transform:uppercase;color:var(--muted)}
.ea-meta .v{font-weight:bold}
.ea-btn{display:inline-block;font:inherit;font-weight:bold;background:var(--teal);color:var(--on-teal);border:1px solid var(--teal);border-radius:6px;padding:10px 22px;cursor:pointer;text-decoration:none}
.ea-btn:hover{opacity:.88}
.ea-btn.ghost{background:none;color:var(--ink);border-color:var(--border)}.ea-btn.ghost:hover{border-color:var(--teal);opacity:1}
.ea-sec{margin-bottom:52px;scroll-margin-top:80px}
.ea-num{font-family:'Syne',sans-serif;letter-spacing:.2em;text-transform:uppercase;color:var(--eyebrow);margin-bottom:12px}
.ea-h2{font-family:'Syne',sans-serif;font-size:22px;font-weight:700;margin-bottom:20px;border-bottom:1px solid var(--border);padding-bottom:10px}
.ea-root p{margin-bottom:16px}
.ea-box{font-size:16px;margin:4px 0 20px;padding:10px 14px;border-radius:4px;line-height:1.5}
.ea-box.good{background:var(--good);color:var(--accent);border-left:3px solid var(--teal)}
.ea-box.bad{background:var(--warnbg);color:var(--warn);border-left:3px solid var(--warn)}
.ea-root:not([data-theme=dark]) .ea-card,.ea-root:not([data-theme=dark]) .ea-ind{position:relative;background:#fff;border:1px solid var(--border);border-radius:10px;box-shadow:var(--rest);transition:transform .22s ease,box-shadow .22s ease,background-color .22s ease,border-color .22s ease}
.ea-root:not([data-theme=dark]) .ea-card:hover,.ea-root:not([data-theme=dark]) .ea-ind:hover{transform:translateY(-3px);box-shadow:var(--lift);background:#f6f6f4;z-index:2}
.ea-root:not([data-theme=dark]) .ea-sum,.ea-root:not([data-theme=dark]) .ea-form{background:#fff;border-radius:10px;box-shadow:var(--rest)}
.ea-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.ea-card{border:1px solid var(--border);border-radius:6px;overflow:hidden;background:var(--paper)}
.ea-card h3{font-family:'Syne',sans-serif;font-size:16px;font-weight:700;padding:14px 16px 10px;border-bottom:1px solid var(--border);background:var(--bg2)}
.ea-card p{padding:14px 16px;margin:0}
.ea-list{display:flex;flex-direction:column;gap:16px}
.ea-ind{border:1px solid var(--border);border-radius:6px;padding:16px;background:var(--paper)}
.ea-ind h3{font-family:'Syne',sans-serif;font-size:16px;font-weight:700;margin-bottom:8px}
.ea-ind p{margin-bottom:10px}
.ea-ind .use{color:var(--muted);font-style:italic;padding:8px 10px;background:var(--bg2);border-radius:4px;margin:0}
.ea-tbl{overflow-x:auto}
.ea-tbl table{width:100%;border-collapse:collapse}
.ea-tbl th{font-family:'Syne',sans-serif;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);text-align:left;padding:8px 10px;border-bottom:2px solid var(--border);background:var(--bg2);white-space:nowrap}
.ea-tbl td{padding:10px;border-bottom:1px solid var(--border);vertical-align:top}
.ea-tbl tr:last-child td{border-bottom:none}
.ea-pill{font-family:'Syne',sans-serif;font-weight:700;padding:2px 8px;border-radius:3px;background:var(--good);color:var(--accent);white-space:nowrap}
.ea-sum{background:var(--bg2);border:1px solid var(--border);border-radius:8px;padding:24px}
.ea-sum h3{font-family:'Syne',sans-serif;font-size:15px;font-weight:700;margin-bottom:16px}
.ea-row{display:flex;justify-content:space-between;gap:16px;border-bottom:1px solid var(--border);padding-bottom:8px;margin-bottom:8px}
.ea-row .k{color:var(--muted);flex-shrink:0;width:130px}.ea-row .val{text-align:right;font-weight:bold;flex:1}
.ea-sum .ea-btn{margin-top:16px}
.ea-form{background:var(--bg2);border:1px solid var(--border);border-radius:8px;padding:24px;display:grid;gap:16px}
.ea-form label{display:grid;gap:4px;font-weight:bold}
.ea-form input,.ea-form select,.ea-form textarea{font:inherit;font-weight:normal;color:var(--ink);background:var(--paper);border:1px solid var(--border);border-radius:4px;padding:9px 12px;width:100%}
.ea-form textarea{min-height:120px;resize:vertical}
.ea-two{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.ea-form .chk{display:flex;gap:10px;align-items:flex-start;font-weight:normal;font-size:14px;color:var(--muted);line-height:1.5}
.ea-form .chk input{width:auto;margin-top:4px}
.ea-hp{position:absolute;left:-9999px}
.ea-root p a:not(.ea-btn),.ea-root .ea-box a:not(.ea-btn),.ea-root .chk a{color:var(--accent);font-weight:600;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:3px}
.ea-root p a:not(.ea-btn):hover,.ea-root .ea-box a:not(.ea-btn):hover,.ea-root .chk a:hover{text-decoration-thickness:2px}
.ea-root a.ea-btn{color:var(--on-teal);text-decoration:none}
.ea-root a.ea-btn.ghost{color:var(--ink)}
.ea-box .ea-btn{margin-top:12px}
@media(max-width:600px){.ea-page{padding:64px 20px 60px}.ea-grid,.ea-two{grid-template-columns:1fr}.ea-meta{gap:20px}.ea-title{font-size:30px}}
@media(max-width:600px){
  .ea-page{padding:60px 16px 48px}
  .ea-cover{padding:34px 0 30px;margin-bottom:30px}
  .ea-tag{font-size:.68rem;letter-spacing:.15em;margin-bottom:12px}
  .ea-title{font-size:clamp(26px,7.2vw,31px);line-height:1.12;overflow-wrap:anywhere}
  .ea-sub{font-size:16px;line-height:1.55;margin-bottom:22px}
  .ea-meta{display:grid;grid-template-columns:1fr 1fr;gap:14px 18px}
  .ea-meta .l{font-size:.65rem;letter-spacing:.1em}
  .ea-meta .v{font-size:.9rem;line-height:1.4}
  .ea-sec{margin-bottom:36px;scroll-margin-top:68px}
  .ea-num{font-size:.68rem;letter-spacing:.15em;margin-bottom:8px}
  .ea-h2{font-size:19px;line-height:1.25;margin-bottom:14px;padding-bottom:8px}
  .ea-root{font-size:15px;line-height:1.65}
  .ea-root p{margin-bottom:13px}
  .ea-grid{gap:10px}
  .ea-card h3{font-size:15px;padding:12px}
  .ea-card p{padding:12px;font-size:.92rem;line-height:1.6}
  .ea-ind{padding:13px}
  .ea-ind h3{font-size:15px}
  .ea-ind p{font-size:.92rem;line-height:1.6}
  .ea-sum{padding:16px}
  .ea-sum h3{font-size:14px;margin-bottom:12px}
  .ea-row{gap:8px;padding-bottom:7px;margin-bottom:7px;font-size:.9rem}
  .ea-row .k{width:96px;white-space:normal}
  .ea-row .val{min-width:0;overflow-wrap:anywhere}
  .ea-form{padding:15px;gap:13px}
  .ea-form label{font-size:.9rem}
  .ea-form input,.ea-form select,.ea-form textarea{font-size:16px;padding:10px 11px;min-width:0}
  .ea-form textarea{min-height:108px}
  .ea-form .chk{font-size:12px;gap:8px}
  .ea-btn{max-width:100%;white-space:normal;text-align:center;padding:10px 15px;font-size:.92rem}
  .ea-pill{white-space:normal;display:inline-block}
  .ea-tbl{max-width:100%;overscroll-behavior-x:contain}
  .ea-tbl table{font-size:.82rem}
  .ea-tbl th,.ea-tbl td{padding:8px}
}
@media(max-width:380px){
  .ea-page{padding-left:13px;padding-right:13px}
  .ea-meta{gap:12px}
  .ea-meta .v{font-size:.84rem}
  .ea-form,.ea-sum{padding:12px}
  .ea-row .k{width:82px}
}
`;
export const Sec = ({ n, title, id, children }) => (
  <section className="ea-sec" id={id}><div className="ea-num">{n}</div><h2 className="ea-h2">{title}</h2>{children}</section>
);

// ── PAGE ──────────────────────────────────────────────────────────────────────
export default function StrategyAutomationPage({ onSubmitLead }) {
  const [theme] = useTheme();
  const navType = useNavigationType();

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const track = (name, props = {}) => { if (window.plausible) window.plausible(name, { props }); };
  const quote = (label) => (e) => { if (e) e.preventDefault(); track('CTA Click', { button: label }); go('quote'); };

  useEffect(() => {
    document.title = 'Strategy Automation | Custom Expert Advisor Development | Qire Agency';
    if (navType !== 'POP') window.scrollTo(0, 0);
    const prev = document.body.style.background;
    const font = document.createElement('link');
    font.rel = 'stylesheet';
    font.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap';
    document.head.appendChild(font);
    const script = document.createElement('script');
    script.defer = true; script.dataset.domain = 'qireagency.com'; script.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(script);
    window.plausible = window.plausible || function () { (window.plausible.q = window.plausible.q || []).push(arguments); };
    return () => { document.body.style.background = prev; [font, script].forEach(n => n.parentNode && n.parentNode.removeChild(n)); };
  }, []);

  useEffect(() => { document.body.style.background = theme === 'dark' ? '#050506' : '#fff'; }, [theme]);


  return (
    <>
    <SiteHeader current="automation" cta={{ label: 'Get a Quote', onClick: () => quote('Get a Quote (Header)')() }} />
    <div className="ea-root" data-theme={theme}>
      <style>{CSS}</style>
      <div className="ea-page">
        <header className="ea-cover">
          <div className="ea-tag">Strategy <span>Automation</span></div>
          <h1 className="ea-title">Your trading strategy, built into a tested Expert Advisor.</h1>
          <p className="ea-sub">Fixed price. Fixed delivery date. Backtest report included.</p>
          <div className="ea-meta">{META.map(([l, v]) => <div key={l}><span className="l">{l}</span><span className="v">{v}</span></div>)}</div>
          <p style={{ marginTop: 28, marginBottom: 0 }}><button className="ea-btn" onClick={quote('Get a Quote (Cover)')}>Get a Quote →</button></p>
        </header>

        <Sec n="01" title="Overview">
          <p>If you trade by clear rules, a program can follow them for you. You give us your strategy in plain language. We write a specification for you to approve, then build and test an Expert Advisor for MetaTrader 5 or 4.</p>
          <p>Rules only work when they are followed every time. An Expert Advisor follows them every time: no hesitation, no missed entries, and the same logic for every student or member who runs it.</p>
          <div className="ea-box good">Q-Trader EA and Q-Panel are published on MQL5 Market. Since 2021 we have built trading tools for clients on Upwork and Freelancer.com.</div>
        </Sec>

        <Sec n="02" title="What's included">
          <div className="ea-grid">{GET.map(([t, d]) => <div className="ea-card" key={t}><h3>{t}</h3><p>{d}</p></div>)}</div>
        </Sec>

        <Sec n="03" title="How it works">
          <div className="ea-tbl"><table>
            <thead><tr><th>Step</th><th>What happens</th><th>Payment</th></tr></thead>
            <tbody>{STEPS.map(([t, d, p], i) => <tr key={t}><td><strong>{i + 1}. {t}</strong></td><td>{d}</td><td><span className="ea-pill">{p}</span></td></tr>)}</tbody>
          </table></div>
        </Sec>

        <Sec n="04" title="Who it's for">
          <div className="ea-list">{WHO.map(([t, d, u]) => <div className="ea-ind" key={t}><h3>{t}</h3><p>{d}</p><p className="use">{u}</p></div>)}</div>
          <p style={{ marginTop: 16, color: 'var(--muted)' }}>Brokers, IBs and fintech teams can also ask for custom trading tools, quoted per project. Choose Broker or IB, or Fintech team, under “I am a” in the form below.</p>
        </Sec>

        <Sec n="05" title="Pricing and terms">
          <div className="ea-sum">
            <h3>A fixed quote with clear terms</h3>
            {TERMS.map(([k, v], i) => <div className="ea-row" key={k} style={i === TERMS.length - 1 ? { borderBottom: 0, marginBottom: 0, paddingBottom: 0 } : null}><span className="k">{k}</span><span className="val">{v}</span></div>)}
            <button className="ea-btn" onClick={quote('Get a Quote (Terms)')}>Get a Quote →</button>
          </div>
        </Sec>

        <Sec n="06" title="Frequently asked questions">
          <div className="ea-list">{FAQ.map(([q, a]) => <div className="ea-ind" key={q}><h3>{q}</h3><p style={{ color: 'var(--muted)', margin: 0 }}>{a}</p></div>)}</div>
        </Sec>

        <Sec n="07" title="Request a quote" id="quote">
          <p>Send the form and we will reply within 24 hours with a fixed quote, or an honest answer that it cannot be automated. Please do not paste confidential rules here, as we sign an NDA first. Prefer to talk? Choose the call option in the form: you send your details first, then pick a time.</p>
          <LeadForm onSubmitLead={onSubmitLead} variant="ea" formName="Strategy Automation page" source="Strategy Automation page" service="Strategy Automation" subject="Custom Trading Bot / EA (Strategy Automation)" track={track} />
        </Sec>

        

        
      </div>

    </div>
    <SiteFooter />
    <FloatingCta label="Get a Quote" onClick={() => quote('Get a Quote (Floating)')()} hideOn={['quote', 'site-footer']} />
    </>
  );
}